import React from 'react';
import { Table, Button, Modal, CheckBox } from '@roo/roo';
import request from '@/utils/request';
import utils from '@/utils/utils';
import url from '@/utils/url';
import StatisticDataPage from './statisticDataPage';
import consts from './consts';

import './list.scss';
import SetPlaybackModal, { PlaybackLiveData } from './setPlaybackModal';

// @ts-ignore
const CheckBoxGroup = CheckBox.Group;

interface IState {
    liveList: urls.live.ILiveListItem[];
    loading: boolean;
    productList: urls.live.IProduct[];
    liveRoomId: number; // 直播间id，一个主播(服务商)有一个直播间，可以有不同的liveId，不同的直播id
    invalidCreate: boolean; // 不可创建标志，不可以再申请，状态包括：待审核、审核通过、预告、直播中
    playBackVisible: boolean; // 设置回放弹窗是否展示
    playBackLive: PlaybackLiveData; // 回放直播信息
}

interface IProps {
    history: any;
    editData: any;
}

class ListContent extends React.Component<IProps, IState> {
    columns = [
        {
            label: '直播主题',
            prop: 'live.title',
            width: 100,
            render: (text: any, record: urls.live.ILiveListItem) => record.live.title,
        },
        {
            label: '直播简介',
            prop: 'live.introduction',
            width: '200px',
            render: (text: any, record: urls.live.ILiveListItem) => record.live.introduction,
        },
        {
            label: '宣传封面',
            prop: 'previewPicUrl',
            width: '300px',
            render: (text: any, record: urls.live.ILiveListItem) => {
                const { live } = record;
                return (
                    <div className="live-cover">
                        <img alt="preview" className="live-cover-pic1" src={live.previewPicUrl} />
                        <img alt="cover" className="live-cover-pic2" src={live.coverPicUrl} />
                    </div>
                );
            },
        },
        {
            label: '直播时间',
            prop: 'live.startTime',
            width: 160,
            render: (text: any, record: urls.live.ILiveListItem) => {
                const startTime = utils.timestampToMinutes(record.live.startTime);
                const endTime = utils.timestampToMinutes(record.live.endTime);
                let expireText = '暂无';
                if (record.live.state >= consts.LIVE_STATE.LIVE_END) {
                    const playbackExpire = utils.timestampToMinutes(record.live.playbackExpire);
                    expireText = `${playbackExpire.date} ${playbackExpire.time}`;
                }
                return (
                    <div className="live-times">
                        <div>{`开始时间：${startTime.date} ${startTime.time}`}</div>
                        <div>{`结束时间：${endTime.date} ${endTime.time}`}</div>
                        <div>{`过期时间：${expireText}`}</div>
                    </div>
                );
            },
        },
        {
            label: '售卖商品',
            prop: 'items',
            width: 160,
            render: (text: urls.live.IProduct[]) => text.map(item => item.name).join('、'),
        },
        {
            label: '状态',
            prop: 'live.state',
            width: 77,
            render: (text: any, record: urls.live.ILiveListItem) => (
                <div>
                    <div>{this.getOpts(record.live.state).text}</div>
                    {record.live.state === consts.LIVE_STATE.REVIEW_UNPASS ? (
                        <Button
                            size="mini"
                            className="mgt10"
                            onClick={() => this.showUnpassReason(record.live.id)}
                        >
                            查看原因
                        </Button>
                    ) : null}
                </div>
            ),
        },
        {
            label: '操作',
            prop: 'option',
            width: '120px',
            render: (text: any, record: urls.live.ILiveListItem) => {
                const { live } = record;
                const opts = this.getOpts(live.state).opts || [];
                return opts.map((item: any, k) => (
                    <Button
                        key={k}
                        size="mini"
                        style={{
                            marginRight: 5,
                            marginBottom: 8,
                        }}
                        onClick={() => {
                            item.onClick(live, record);
                        }}
                    >
                        {item.text}
                    </Button>
                ));
            },
        },
    ];

    constructor(props: IProps) {
        super(props);
        // const urlObj: any = utils.urlParse();
        this.state = {
            liveList: [],
            loading: false,
            productList: [],
            liveRoomId: 0,
            invalidCreate: false,
            playBackVisible: false,
            playBackLive: { id: 0, type: 0, title: '' },
        };
    }

    async componentDidMount() {
        this.getLiveList();
        this.getProductList();
    }

    // 未通过原因
    showUnpassReason = async (liveId: number) => {
        const res = await request.get('/api/live/rejectReason', {
            busId: url.commonParams.providerId,
            busLine: 1,
            liveId,
        });
        Modal.confirm({
            title: '未通过原因',
            children: res.data.remark,
            showCancelButton: false,
        });
    };

    // 获取服务商下对应的商品列表
    getProductList = async () => {
        const res = await request.get('/api/live/itemList', {
            busId: url.commonParams.providerId,
            busLine: 1, // 业务线:1服务市场,2闪购,3快驴 （默认服务市场）
        });
        const list = res.data || [];
        this.setState({
            productList: list.map(({ id, name, logo }: any) => ({
                id,
                name,
                logo,
            })),
        });
    };

    // 配置商品
    configItems = async (liveId: number, itemidList: number[]) => {
        if (itemidList.length > 10) {
            utils.toast('warning', '最多配置10个商品');
            return;
        }
        await request.post('/api/live/configItems', {
            busId: url.commonParams.providerId,
            busLine: 1, // 业务线:1服务市场,2闪购,3快驴 （默认服务市场）
            liveId,
            itemidList,
        });
        utils.toast('success', '添加成功');
        this.getLiveList();
    };

    // 获取直播列表，暂时不做分页
    getLiveList = async () => {
        this.setState({
            loading: true,
        });
        const res = await request.get('/api/live/list', {
            busId: url.commonParams.providerId,
            busLine: 1,
            pageNo: 1,
            pageSize: 80,
        });
        // 判断是否可以点击创建直播，以下不可以再申请，状态包括：待审核、审核通过、预告、直播中
        const liveList: urls.live.ILiveListItem[] = res.data.list || [];
        let invalidCreate = false;
        for (let i = 0; i < liveList.length; i++) {
            const { state } = liveList[i].live;
            if (
                state === consts.LIVE_STATE.REVIEWING ||
                state === consts.LIVE_STATE.REVIEW_PASS ||
                state === consts.LIVE_STATE.NOTICE ||
                state === consts.LIVE_STATE.LIVING
            ) {
                invalidCreate = true;
                break;
            }
        }
        this.setState({
            liveList,
            loading: false,
            invalidCreate,
        });
    };

    /**
     * 更新直播状态
     * @param liveId
     * @param targetState 目标状态:4:预告,5:直播中,6:直播结束,7:回放中
     * @param playBackStartTime 设置回放的时候才需要，开始时间戳
     * @param playBackEndTime
     */
    updateLiveState = async (
        liveId: number,
        targetState: number,
        playBackStartTime?: number,
        playBackEndTime?: number,
        title?: string,
        duration?: number,
    ) => {
        await request.post(
            '/api/live/stateDeliver',
            {
                busId: url.commonParams.providerId,
                busLine: 1,
                liveId,
                targetState,
                playBackStartTime,
                playBackEndTime,
                title,
                duration,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
        utils.toast('success', '操作成功');
        this.getLiveList();
    };

    // 获取预告、播放等连接
    showUrls = (live: urls.live.ILive) => {
        const liveUrl = url.getLiveUrl({
            liveId: live.id,
            streamId: live.streamId,
            liveType: live.type,
        });
        const pushUrl = url.getPushUrl(live.pushUrl);

        Modal.confirm({
            title: '获取链接',
            style: { width: '700px' },
            children: (
                <div className="live-window">
                    <div style={{ marginBottom: 8 }}>
                        请您先阅读
                        <a
                            href="https://collegewm.meituan.com/?r=1#/college/detail?id=2578&contentType=1&utm_content_source=FWSHT"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            直播操作手册
                        </a>
                        ，并参照手册中的教程进行操作。
                    </div>
                    <p>以下是直播过程中所需要用到的配置地址：</p>
                    <p className="my-form-item">
                        <div className="item-label">直播密钥：</div>
                        <div className="item-content">{pushUrl.key}</div>
                    </p>
                    <p className="my-form-item">
                        <div className="item-label">服务器地址：</div>
                        <div className="item-content">{pushUrl.serverUrl}</div>
                    </p>
                    <p className="my-form-item">
                        <div className="item-label">播放端正式地址：</div>
                        <div className="item-content">{liveUrl.liveUrl}</div>
                    </p>
                    <p className="my-form-item">
                        <div className="item-label">直播预告地址：</div>
                        <div className="item-content">{liveUrl.noticeUrl}</div>
                    </p>
                    <p className="my-form-item">
                        <div className="item-label">播放端回放地址：</div>
                        <div className="item-content">
                            {live.state < 7
                                ? '您还未开启回放'
                                : `${liveUrl.playbackUrl}&${live.playbackParam}`}
                        </div>
                    </p>
                </div>
            ),
            backdropClosable: false,
            showCancelButton: false,
            confirmText: '我知道了',
            onConfirm: () => { },
        });
    };

    // 开始直播
    beginLive = (live: urls.live.ILive) => {
        Modal.confirm({
            title: '提示',
            children: '请确保您的直播已调试完毕，开始直播后，用户将看到您的直播内容',
            backdropClosable: false,
            size: 'sm',
            onConfirm: () => {
                this.updateLiveState(live.id, consts.LIVE_STATE.LIVING);
            },
        });
    };

    // 结束直播
    endLive = (live: urls.live.ILive) => {
        Modal.confirm({
            title: '提示',
            children: '结束直播后，用户将不再看到您的直播内容',
            backdropClosable: false,
            size: 'sm',
            onConfirm: () => {
                this.updateLiveState(live.id, consts.LIVE_STATE.LIVE_END);
            },
        });
    };

    // 关闭直播
    closeLive = (live: urls.live.ILive) => {
        Modal.confirm({
            title: '提示',
            children: '确认关闭吗？',
            backdropClosable: false,
            size: 'sm',
            onConfirm: () => {
                this.updateLiveState(live.id, consts.LIVE_STATE.CLOSE);
            },
        });
    };

    // 修改直播
    editLive = (live: urls.live.ILive) => {
        this.props.history.push(`/live/edit?liveId=${live.id}`);
    };

    // 配置商品
    updateProduct = (live: urls.live.ILive) => {
        const { productList } = this.state;
        let selectIds: any = live.itemIds || [];
        Modal.confirm({
            title: '请选择需要在直播间售卖的商品',
            children: (
                <div className="live-window checkbox">
                    <CheckBoxGroup
                        defaultValue={live.itemIds}
                        onChange={(ids: any) => {
                            selectIds = ids;
                        }}
                    >
                        {productList.map(item => (
                            <CheckBox key={item.id} value={item.id}>
                                {item.name}
                            </CheckBox>
                        ))}
                    </CheckBoxGroup>
                </div>
            ),
            onConfirm: () => {
                this.configItems(live.id, selectIds);
            },
            backdropClosable: false,
        });
    };

    // 开启预告
    openNotice = (live: urls.live.ILive) => {
        Modal.confirm({
            title: '提示',
            children: '开启预告后，您的直播将以预告的形式展示给用户',
            backdropClosable: false,
            size: 'sm',
            onConfirm: () => {
                this.updateLiveState(live.id, consts.LIVE_STATE.NOTICE);
            },
        });
    };

    // 回访时间不可选时间段
    disabledTime = (time: number, type: 'hour' | 'minute' | 'second', hhmmssObj: any) => {
        let disable = false;
        // 只判断小时。由于分钟、秒没有好的方法去限制，所以只进行简单判断，如最大是05:10:24，如过此事选的4点，则所有的分钟都可选
        if (type === 'hour' && time > hhmmssObj.hh) {
            disable = true;
        }
        return disable;
    };

    // 设置回放
    setPlayBack = async (live: urls.live.ILive) => {
        this.setState({
            playBackVisible: true,
            playBackLive: live,
        });
    };

    // 展示统计数据
    showStatisticData = (live: urls.live.ILive) => {
        Modal.confirm({
            title: '查看数据',
            children: <StatisticDataPage liveId={live.id} />,
            backdropClosable: false,
            showCancelButton: false,
            confirmText: '我知道了',
            style: { width: 1200 },
            onConfirm: () => { },
        });
    };

    // 获取状态、操作
    getOpts = (state: number) => {
        const beginLive = {
            text: '开始直播',
            onClick: this.beginLive,
        };
        const endLive = {
            text: '结束直播',
            onClick: this.endLive,
        };
        const showUrlsOpt = {
            text: '直播配置',
            onClick: this.showUrls,
        };
        const closeLive = {
            text: '关闭',
            onClick: this.closeLive,
        };
        const editLive = {
            text: '修改',
            onClick: this.editLive,
        };
        const updateProduct = {
            text: '添加商品',
            onClick: this.updateProduct,
        };
        const openNotice = {
            text: '开启预告',
            onClick: this.openNotice,
        };
        const setPlayBack = {
            text: '设置回放',
            onClick: this.setPlayBack,
        };
        const showStatisticData = {
            text: '统计数据',
            onClick: this.showStatisticData,
        };
        // 直播状态 1:审核中 2:审核驳回 3:通过 4:预告 5:直播中  6:直播结束 7:回放中 8:过期
        const statusOptMap: any = {
            [consts.LIVE_STATE.REVIEWING]: {
                text: '审核中',
                opts: [closeLive],
            },
            [consts.LIVE_STATE.REVIEW_UNPASS]: {
                text: '审核未通过',
                opts: [editLive],
            },
            [consts.LIVE_STATE.REVIEW_PASS]: {
                text: '审核通过',
                opts: [showUrlsOpt, updateProduct, openNotice, editLive, closeLive],
            },
            [consts.LIVE_STATE.NOTICE]: {
                text: '预告',
                opts: [beginLive, updateProduct, showUrlsOpt, closeLive],
            },
            [consts.LIVE_STATE.LIVING]: {
                text: '直播中',
                opts: [endLive, showStatisticData, updateProduct, showUrlsOpt],
            },
            [consts.LIVE_STATE.LIVE_END]: {
                text: '已结束',
                opts: [setPlayBack, showUrlsOpt, showStatisticData],
            },
            [consts.LIVE_STATE.PLAY_BACK]: {
                text: '回放中',
                opts: [setPlayBack, showStatisticData, updateProduct, showUrlsOpt],
            },
            [consts.LIVE_STATE.EXPIRE]: {
                text: '过期',
                opts: [showStatisticData],
            },
            [consts.LIVE_STATE.CLOSE]: {
                text: '关闭',
                opts: [showStatisticData],
            },
            [consts.LIVE_STATE.OFFLINE]: {
                text: '下线',
                opts: [showStatisticData],
            },
        };
        return statusOptMap[state];
    };

    renderSetPlaybackModal = () => {
        const { state } = this;
        return state.playBackVisible ? (
            <SetPlaybackModal
                visible={state.playBackVisible}
                live={state.playBackLive}
                updateLiveState={this.updateLiveState}
                onClose={() => {
                    this.setState({
                        playBackVisible: false,
                    });
                }}
            />
        ) : null;
    };

    render() {
        const { state, props } = this;

        return (
            <div className="list-content">
                <div className="list-header">
                    <a
                        href="https://collegewm.meituan.com/?r=1#/college/detail?id=2578&contentType=1&utm_content_source=FWSHT"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="live-handbook"
                    >
                        直播操作手册
                    </a>
                    <Button
                        onClick={() => {
                            if (state.invalidCreate) {
                                utils.toast('warn', '您已有进行中的直播，请勿重新申请');
                                return;
                            }
                            props.history.push('/live/edit');
                        }}
                    >
                        创建直播
                    </Button>
                </div>
                <Table
                    border
                    columns={this.columns}
                    data={state.liveList}
                    emptyComponent="暂无数据"
                />
                {this.renderSetPlaybackModal()}
            </div>
        );
    }
}

export default ListContent;
